import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher, useAuth, useIsDesktop } from '@/shared';
import { Bell, Box, LogOut, Menu, Settings, Trophy, X } from 'lucide-react';
import { theme } from '@shared/styles/theme';
import { formatNumber } from '@shared/utils/formatNumber';
import { getFirstGrapheme } from '@shared/utils/stringUtils';
import { styles } from './Header.styles';

/**
 * Header component with Minecraft-themed design.
 * Responsive: shows points on desktop, simplified on mobile.
 * On mobile (<= 430px): logo + avatar + hamburger button.
 * Hamburger opens a drawer with notifications, language switcher, and logout.
 */
export const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation('common');
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const userName = user?.name || localStorage.getItem('userName') || t('user');
  const userInitial = getFirstGrapheme(userName).toUpperCase();
  const totalPoints = user?.totalPoints ?? 0;

  const handleLogout = () => {
    setIsMenuOpen(false);
    logout();
    navigate('/login');
  };

  const handleNotificationsClick = () => {
    setIsMenuOpen(false);
    navigate('/notifications');
  };

  if (isDesktop) {
    return (
      <header style={styles.headerDesktop}>
        <div style={styles.containerDesktop}>
          {/* Logo */}
          <div style={styles.logoSection}>
            <div style={styles.logoBox}>
              <Box size={28} color={theme.colors.text.white} strokeWidth={1.5} />
            </div>
            <h1 style={styles.title} className="pixel-font">
              MathQuest
            </h1>
          </div>

          {/* Right side icons */}
          <output style={styles.rightSection}>
            {/* Notifications Bell */}
            <button
              onClick={() => navigate('/notifications')}
              style={styles.notifButton}
              aria-label={t('navigation.notifications')}
            >
              <Bell size={20} color={theme.colors.text.white} strokeWidth={2} aria-hidden="true" />
            </button>

            {/* Language Toggle */}
            <LanguageSwitcher compact />

            {/* Admin Panel — only visible to admins */}
            {user?.role === 'Admin' && (
              <button
                onClick={() => navigate('/admin')}
                style={styles.settingsBox}
                aria-label={t('navigation.adminPanel')}
              >
                <Settings
                  size={20}
                  color={theme.colors.text.white}
                  strokeWidth={2}
                  aria-hidden="true"
                />
              </button>
            )}

            {/* Points - Desktop only */}
            <output
              style={styles.pointsBox}
              aria-label={`${formatNumber(totalPoints)} ${t('common.points')}`}
            >
              <Trophy size={18} color={theme.colors.accent.yellow800} aria-hidden="true" />
              <span style={styles.pointsText}>{formatNumber(totalPoints)}</span>
            </output>

            {/* User Avatar (fallback initial) */}
            <div style={styles.avatarBox} aria-label={userName}>
              <span style={styles.avatarText} aria-hidden="true">
                {userInitial}
              </span>
            </div>

            {/* Log Out Button */}
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              style={styles.logoutButton}
              aria-label={t('actions.logout')}
            >
              <LogOut size={16} color={theme.colors.text.white} aria-hidden="true" />
            </button>
          </output>
        </div>
      </header>
    );
  }

  // Mobile layout: logo + avatar + hamburger
  return (
    <>
      <header style={styles.headerMobile}>
        <div style={styles.containerMobile}>
          {/* Logo */}
          <div style={styles.logoSection}>
            <div style={styles.logoBox}>
              <Box size={28} color={theme.colors.text.white} strokeWidth={1.5} />
            </div>
            <h1 style={styles.title} className="pixel-font">
              MathQuest
            </h1>
          </div>

          {/* Mobile right: avatar + hamburger */}
          <div style={styles.mobileRightSection}>
            {/* User Avatar */}
            <div style={styles.avatarBox} aria-label={userName}>
              <span style={styles.avatarText} aria-hidden="true">
                {userInitial}
              </span>
            </div>

            {/* Hamburger button */}
            <button
              onClick={() => setIsMenuOpen((prev) => !prev)}
              style={styles.hamburgerButton}
              aria-label={isMenuOpen ? t('actions.closeMenu') : t('actions.openMenu')}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <X size={20} color={theme.colors.text.white} strokeWidth={2} aria-hidden="true" />
              ) : (
                <Menu
                  size={20}
                  color={theme.colors.text.white}
                  strokeWidth={2}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Drawer overlay — transparent, catches outside clicks */}
      {isMenuOpen && (
        <div style={styles.drawerOverlay} onClick={() => setIsMenuOpen(false)} aria-hidden="true" />
      )}

      {/* Drawer panel */}
      {isMenuOpen && (
        <nav style={styles.drawer} aria-label={t('navigation.mobileMenu')}>
          {/* Notifications */}
          <div style={styles.drawerItem}>
            <button
              style={styles.drawerItemButton}
              onClick={handleNotificationsClick}
              aria-label={t('navigation.notifications')}
            >
              <Bell size={20} color={theme.colors.accent.blue} strokeWidth={2} aria-hidden="true" />
              <span style={styles.drawerItemLabel}>{t('navigation.notifications')}</span>
            </button>
          </div>

          {/* Language Switcher */}
          <div style={styles.drawerItem}>
            <LanguageSwitcher compact />
            <span style={styles.drawerItemLabel}>{t('navigation.language')}</span>
          </div>

          {/* Logout */}
          <div style={styles.drawerItemLast}>
            <button
              style={styles.drawerLogoutButton}
              onClick={handleLogout}
              aria-label={t('actions.logout')}
            >
              <LogOut size={16} color={theme.colors.text.white} aria-hidden="true" />
              <span>{t('actions.logout')}</span>
            </button>
          </div>
        </nav>
      )}
    </>
  );
};
